import { IUser } from "../../auth/authentication/models/auth-response";
import { LabelOrigin } from "../../settings/models/settings.models";
import { IAccountMailbox } from "./account.models";
import { IConversationTimelineObject } from "./timeline.models";

/** Minimal customer (location) info for conversation association display */

export interface IConversationCustomerOrder {
  id: number;
  name: string;
  date: string;
  total_amount: number;
  status: string;
}

export interface IConversationThreadContext {
  company_name: string;
  company_currency?: {
    id: number;
    name: string;
    symbol: string;
    position: "after" | "before";
  };
  sender: {
    email: string;
    name: string;
    is_contact: boolean;
  };
  kpis: {
    total_orders_amount: number;
    average_order_value: number;
    average_sku_count_per_order: number;
  };
  last_orders: IConversationCustomerOrder[];
  products_kpis: {
    favorite_brand: string | null;
    products_bought_count: number;
  };
}

/** Product item returned by thread customer products API (sidebar listings). */
export interface IConversationCustomerProduct {
  product_tmpl_id: number;
  product_id: number;
  pricelist_id: number;
  name: string;
  brand: string | false;
  sku: string | false;
  barcode: string | false;
  sales_price: number;
  available_quantity: number;
}

/** Catalog item returned by thread customer catalogs API (sidebar listings). */
export interface IConversationCustomerCatalog {
  id: number;
  name: string;
  image: string | null;
  nbr_products: number;
}

export interface IConversationCustomer {
    id: number;
    company_name: string;
    location: string;
}

export interface IConversation {
    id: number;
    subject: string;
    sender: string;
    sender_email: string;
    mailbox: IAccountMailbox;
    provider_thread_id: string;
    last_message_at: string;
    message_count: number;
    attachment_count: number;
    is_read: boolean;
    assigned_to_id: string;
    urgency: string;
    /**
     * Effective thread urgency: true when any message is AI-urgent, unless an
     * operator override says otherwise. The override wins, so a thread can be
     * `false` while still containing an `is_urgent` message — that is expected.
     */
    is_urgent?: boolean;
    /** Category of the most recent classified message. */
    classification: ConversationClassification;
    /** All distinct categories across the thread's messages, most recent first. */
    categories?: ConversationClassification[];
    /** Server-side vote of the thread's messages; absent when never resolved, unlike `'unknown'`. */
    counterparty?: ConversationCounterparty;
    /** See `CounterpartyStatus`. */
    counterparty_status?: CounterpartyStatus;
    /** At least one message was read by the AI against the retained counterparty. */
    counterparty_conflict?: boolean;
    status: ConversationStatus;
    assignee: IUser | null;

    customer: IConversationCustomer | null;

    /**
     * Visible labels applied to the thread. Optional because the MINIMAL thread
     * payload omits the key entirely — absent and empty mean different things,
     * and neither means "this thread has no labels".
     */
    labels?: IConversationLabel[];
}

/**
 * A label the provider put on this thread that Volta has not imported.
 *
 * There is no `wolsal.label` behind it, so it is not in `labels` and cannot be
 * filtered on -- `ref_id` addresses the provider ref, not a label. It is shown
 * on the conversation because that is where a user meets it, and it is drawn
 * differently from a real chip precisely because it does nothing yet.
 */
export interface IPendingProviderLabel {
    ref_id: number;
    name: string;
    external_id: string;
    mailbox_email: string;
}

export interface IConversationDetails extends IConversation {
    thread_context?: IConversationThreadContext | null;
    /** Ordered timeline of messages and events (extensible for mentions, comments, replies). */
    objects?: IConversationTimelineObject[];
    /**
     * Server-derived: a create-order job is still running for this thread (claim
     * set and within TTL). The client re-arms the in-progress skeleton from this
     * on load, so it survives a refresh instead of living only in the store.
     */
    order_creation_in_progress?: boolean;
    /** Provider labels on this thread awaiting a decision. Details only: the
     *  list row does not carry them, and an absent key means "none". */
    pending_provider_labels?: IPendingProviderLabel[];
}

/** A single sent (outbound) message shown in the Sent folder (flat list of messages, not threads). */
export interface ISentMessage {
    id: number;            // Odoo message id (used to highlight the message in the timeline)
    thread_id: number;     // Odoo thread id (used to open the conversation)
    subject: string;
    recipients: string;    // "To" addresses, comma-separated
    sender: string;
    sender_email: string;
    sender_name: string;
    timestamp: string;
    is_read: boolean;
    has_attachments: boolean;
    attachment_count: number;
    mailbox: IAccountMailbox;
}

/** Params for getSentMessages API: filters + pagination */
export interface IGetSentMessagesParams {
    mailbox_ids: number[];
    search_term?: string;
    date_from?: string;
    date_to?: string;
    has_attachment?: boolean;
    page?: number;
    limit?: number;
}

export interface IConversationUpdate {
    is_read?: boolean;
    category?: ConversationClassification;
    status?: ConversationStatus;
    assignee_id?: number | null; // Platform User ID
    customer_id?: number | null; // Company Location
}

/** Reusable filters for listing conversations (mailboxes, categories, statuses, assignees, search key, advanced) */
export interface IConversationListFilters {
    mailbox_ids: number[];
    categories?: ConversationClassification[];
    statuses?: ConversationStatus[];
    assignee_ids?: number[];
    /**
     * Team ids: narrows to threads assigned to any member of ANY of these teams.
     * Omitted when unset -- never sent as an empty list.
     */
    team_ids?: number[];
    search_term?: string;
    from?: string;
    to?: string;
    subject?: string;
    includes_words?: string;
    excludes_words?: string;
    date_from?: string;
    date_to?: string;
    has_attachment?: boolean;
    customer_ids?: number[];
    /**
     * Label ids; a thread matches if it carries AT LEAST ONE. Absent when unset --
     * never `[]`, which the server would read as "matches nothing".
     */
    applied_label_ids?: number[];
    /** Filter on the thread's effective (override-aware) urgency. */
    is_urgent?: boolean;
    /** Filter on the conversation's counterparty. */
    counterparties?: ConversationCounterparty[];
}

/** Body of PUT /inbox/messages/{id}/classification — at least one field required. */
export interface IMessageClassificationUpdate {
    category?: ConversationClassification;
    /** Uppercase intent key, or null to clear it. */
    intent?: MessageIntent | null;
    /** Operator correction of the derived counterparty. */
    counterparty?: ConversationCounterparty;
    /** Location to register the correspondent against; required with `counterparty: 'customer'` (else 409). */
    customer_id?: number;
    /** Declare the correspondent both buys from us and sells to us. */
    dual_role?: boolean;
}

/** Object returned by the message-classification override endpoint. */
export interface IMessageClassificationResult {
    id: number;
    classification: ConversationClassification;
    classification_confidence?: number;
    intent: MessageIntent | null;
    intent_confidence?: number;
    is_urgent?: boolean;
    counterparty?: ConversationCounterparty | null;
    counterparty_confidence?: number;
    counterparty_source?: string | null;
    counterparty_evidence?: string | null;
    counterparty_status?: CounterpartyStatus | null;
    counterparty_model?: string | null;
    counterparty_model_confidence?: number;
    counterparty_conflict?: boolean;
    counterparty_dual_role?: boolean;
}

/** How settled a counterparty is; derived server-side, `NONE` shows no badge. */
export const CounterpartyStatus = {
    CONFIRMED: 'confirmed',
    TO_VERIFY: 'to_verify',
    NONE: 'none',
} as const;

export type CounterpartyStatus = (typeof CounterpartyStatus)[keyof typeof CounterpartyStatus];

/** Object returned by the thread-urgency override endpoint. */
export interface IThreadUrgencyResult {
    id: number;
    is_urgent: boolean;
}

/**
 * A label as a thread payload carries it — four keys, not the whole `ILabel`.
 * `description`, `eligible` and the timestamps are absent from the wire here on
 * purpose, so a list of fifty threads does not carry them for nothing.
 *
 * The API already drops hidden labels from this list, so an empty `labels` is
 * "none applied *that you may see*", not "none applied".
 */
export interface IConversationLabel {
    id: number;
    name: string;
    color: string;
    origin: LabelOrigin;
}

/**
 * Body of `PUT /v1/inbox/threads/<id>/labels`. At least one of the three keys is
 * required, an id may not appear in both lists, and each list is capped at
 * `THREAD_LABEL_IDS_MAX`.
 *
 * `create_and_apply` is a NAME, not an id: the backend resolves it against the
 * existing labels case-insensitively and only creates one when nothing matches.
 * That is why the UI never posts a create followed by an apply — two calls could
 * interleave with another operator's and invent the duplicate the single call
 * exists to avoid.
 */
export interface IThreadLabelUpdate {
    add_label_ids?: number[];
    remove_label_ids?: number[];
    create_and_apply?: string;
}

/**
 * `{thread_id, labels}` from the same route. `labels` is the thread's WHOLE
 * visible set afterwards, not a delta — so the caller replaces rather than
 * merges, and a `create_and_apply` that resolved to an existing label comes back
 * as one entry rather than two.
 */
export interface IThreadLabelsResult {
    thread_id: number;
    labels: IConversationLabel[];
}

/** The backend's per-request ceiling on `add_label_ids` / `remove_label_ids`. */
export const THREAD_LABEL_IDS_MAX = 200;

/** Params for getConversations API: filters + pagination */
export interface IGetConversationsParams extends IConversationListFilters {
    page?: number;
    limit?: number;
}

export const ConversationClassification = {
    ORDER: 'order',
    RFQ: 'rfq',
    SUPPORT: 'support',
    INQUIRY: 'inquiry',
    ADMIN: 'admin',
    SPAM: 'spam',
    OTHER: 'other',
    UNDEFINED: 'undefined'
} as const;

export type ConversationClassification = (typeof ConversationClassification)[keyof typeof ConversationClassification];

/**
 * Fine-grained intent detected by the classifier. The backend treats this as an
 * open uppercase string (`^[A-Z][A-Z_]{0,79}$`) it can extend without a release,
 * so the type stays open. Operators pick from MESSAGE_INTENTS_BY_CATEGORY only;
 * any value the AI invents still renders via `intentLabelKey()`'s fallback.
 */
export type MessageIntent = string;

/**
 * Intents offered per category — the classifier's actual vocabulary. The picker
 * only lists intents valid for the message's current category. Categories absent
 * from this map (`spam`, `other`) are not operator-selectable; they still render
 * when the backend sends them.
 */
export const MESSAGE_INTENTS_BY_CATEGORY: Partial<Record<ConversationClassification, MessageIntent[]>> = {
    [ConversationClassification.ORDER]: [
        'ORDER_PLACING',
        'ORDER_UPDATE',
        'ORDER_CANCELLATION',
        'OTHER',
    ],
    [ConversationClassification.RFQ]: [
        'REQUEST_FOR_QUOTATION',
        'QUOTE_ACCEPTANCE',
        'QUOTE_REJECTION',
        'QUOTE_UPDATE',
        'OTHER',
    ],
    [ConversationClassification.SUPPORT]: [
        'DELIVERY_REQUEST',
        'RETURN_REQUEST',
        'PRODUCT_CLAIM',
        'OTHER',
    ],
    [ConversationClassification.INQUIRY]: [
        'SPECS_REQUEST',
        'DOCUMENTATION_REQUEST',
        'SUBSTITUTE_REQUEST',
        'UPGRADE_REQUEST',
        'DOWNGRADE_REQUEST',
        'OTHER',
    ],
    [ConversationClassification.ADMIN]: [
        'INVOICING',
        'PAYMENT_TERMS',
        'OTHER',
    ],
    [ConversationClassification.UNDEFINED]: [
        'OTHER',
    ],
};

/**
 * Categories an operator can assign. Derived from the intent map so the two can
 * never drift — a category without a defined intent list is not offered.
 */
export const SELECTABLE_CLASSIFICATIONS = Object.keys(MESSAGE_INTENTS_BY_CATEGORY) as ConversationClassification[];

/** Who the external correspondent is; independent of `ConversationClassification`. */
export const ConversationCounterparty = {
    CUSTOMER: 'customer',
    THIRD_PARTY: 'third_party',
    INTERNAL: 'internal',
    UNKNOWN: 'unknown',
} as const;

export type ConversationCounterparty =
    (typeof ConversationCounterparty)[keyof typeof ConversationCounterparty];

/** Counterparties an operator can assign, `unknown` included. */
export const SELECTABLE_COUNTERPARTIES: ConversationCounterparty[] = Object.values(ConversationCounterparty);

export const ConversationStatus = {
    NEW: 'new',
    OPEN: 'open',
    ON_HOLD: 'on_hold',
    CLOSED: 'closed',
} as const;
export type ConversationStatus = (typeof ConversationStatus)[keyof typeof ConversationStatus];

export const ConversationMenuOptions = {
    CATEGORY: 'category',
    STATUS: 'status',
    ASSIGN: 'assign',
    MORE: 'more'
} as const;

export type ConversationMenuOptions = (typeof ConversationMenuOptions)[keyof typeof ConversationMenuOptions];
/** 202 body of `POST /threads/{id}/create-order` — an acknowledgement, never the order. */
export interface ICreateOrderAck {
    thread_id: number;
    status: string;
}
